import type { SyntheticEvent } from 'react'
import React, { useMemo, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, Display, FlexDirection, Modal, Overflow } from '@gousto-internal/citrus-react'
import { useSelector } from 'react-redux'

import { ScrollableBasketModalContent } from '@features/scrollable-basket'
import type { MenuRecipeTileRequestParams, Recipe, RecipeUUID } from '@library/menu-service'
import { queueTrackingEvent } from '@library/tracking-integration'

import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useNumPortions } from 'hooks/basket/useNumPortions'
import { useMaxRecipesForPortion } from 'hooks/menu-boxes/useMaxRecipesForPortion'
import { useStaticArray } from 'hooks/useStaticArray'
import { useBasket } from 'routes/Menu/domains/basket'
import { usePricing } from 'routes/Menu/domains/pricing'
import { useFlexRecipesWeb } from 'routes/Menu/hooks/useFlexRecipesWeb'
import { useRecipeTileRefresh } from 'routes/Menu/hooks/useRecipeTileRefresh'
import {
  getBasketDate,
  getBasketIsFlexOrder,
  getBasketMenuUuid,
  getBasketRecipePositions,
  getChosenAddress,
  getSelectedDeliverySlot,
  isBasketTransactionalOrder,
} from 'selectors/basket'
import { getUserFirstName } from 'selectors/user'

import { RecipeDetail } from '../Search/RecipeDetail/RecipeDetail'
import { BasketProductDetail } from './BasketProductDetail'
import { useBasketUpsell } from './useBasketUpsell'
import { useValueBannerFeatureFlag } from './useValueBannerFeatureFlag'

type ShowBasketEvent = {
  action: 'show_basket'
  properties: {
    menu_uuid: string
    basket_recipe_count: number
    recipes_in_basket: string
  }
}

type ViewBasketModalProps = {
  isCheckoutDisabled: boolean
  isCheckoutPending: boolean
  onCloseModal: () => void
  onCheckout: (e: SyntheticEvent) => void
}

export const ViewBasketModal = ({
  isCheckoutDisabled,
  isCheckoutPending,
  onCloseModal,
  onCheckout,
}: ViewBasketModalProps) => {
  const { basketRecipes } = useBasketRecipes()
  const recipePositions = useSelector(getBasketRecipePositions)
  const { numPortions } = useNumPortions()
  const maxRecipesInBox = useMaxRecipesForPortion(numPortions)
  const { addRecipeByUuid, removeRecipeByUuid, updateRecipePortionsByUuid } = useBasket()
  const { pricing } = usePricing()
  const isValueBannerEnabled = useValueBannerFeatureFlag()
  const isFlexRecipesWebEnabled = useFlexRecipesWeb()
  const isLoadedFlexOrder = useSelector(getBasketIsFlexOrder)
  const isFlexEnabled = isFlexRecipesWebEnabled || isLoadedFlexOrder

  const user = useSelector(getUserFirstName)
  const basketDate = useSelector(getBasketDate)
  const address = useSelector(getChosenAddress)
  const slot = useSelector(getSelectedDeliverySlot)
  const menuUuid = useSelector(getBasketMenuUuid)
  const isTransactionalOrder = useSelector(isBasketTransactionalOrder)
  const [recipeDetailsId, setRecipeDetailsId] = useState<RecipeUUID | null>(null)
  const { refreshRecipeTile } = useRecipeTileRefresh({
    deliveryDate: basketDate,
    menuId: menuUuid,
  })

  const validBasketRecipes = useMemo(() => {
    const recipes = basketRecipes.filter(
      (br): br is typeof br & { recipe: Recipe } => br.recipe != null,
    )
    // Flex baskets are shown in use-by order, but only when every recipe has shelf-life data.
    // If any recipe is missing that data, keep the existing basket order so the list stays predictable.
    const shouldSortByShelfLife =
      isFlexEnabled && recipes.every(({ recipe }) => recipe.shelfLife?.maxDays != null)

    return recipes.sort((a, b) => {
      if (shouldSortByShelfLife) {
        return (
          (a.recipe.shelfLife?.maxDays ?? Number.MAX_SAFE_INTEGER) -
          (b.recipe.shelfLife?.maxDays ?? Number.MAX_SAFE_INTEGER)
        )
      }

      const posA = recipePositions?.indexOf(a.recipe.id) ?? -1
      const posB = recipePositions?.indexOf(b.recipe.id) ?? -1

      return posA - posB
    })
  }, [basketRecipes, recipePositions, isFlexEnabled])

  const uniqueSelectedRecipeIds = useMemo(
    () => Array.from(new Set(validBasketRecipes.map((item) => item.recipe.id))),
    [validBasketRecipes],
  )
  const selectedRecipeIds = useStaticArray(uniqueSelectedRecipeIds)
  const postcode = address?.get('postcode') ?? ''
  const { basketUpsell, productDetail } = useBasketUpsell({
    basketDate,
    menuUuid,
    numPortions,
    postcode,
    selectedRecipeIds,
  })
  const isDetailOpen = Boolean(recipeDetailsId || productDetail)

  const deliverySlot = {
    date: basketDate,
    timeStart: slot?.get('deliveryStartTime'),
    timeEnd: slot?.get('deliveryEndTime'),
  }

  const deliveryAddress = {
    name: address?.get('name') ?? '',
    line1: address?.get('line1') ?? '',
    line2: address?.get('line2') ?? '',
    line3: address?.get('line3') ?? '',
    town: address?.get('town') ?? '',
    postcode: address?.get('postcode') ?? '',
    state: address?.get('state') ?? '',
    county: address?.get('county') ?? '',
  }

  const deliveryPricing = {
    recipeTotal: pricing?.recipeTotal,
    totalDiscount: pricing?.totalDiscount,
    percentageOff: pricing?.percentageOff,
    grossTotal: pricing?.grossTotal,
    averageCostPerRecipe: pricing?.averageCostPerRecipe,
    averageCostPerPortion: pricing?.averageCostPerPortion,
    productTotal: pricing?.productTotal,
    surchargeTotal: pricing?.surchargeTotal,
    surchargeCount: pricing?.surchargeCount,
    total: pricing?.total,
    deliveryTotal: pricing?.deliveryTotal,
    isDeliveryFree: pricing?.isDeliveryFree,
    items: pricing?.items,
  }

  const trackModalOpen = () => {
    datadogRum.addAction('view_basket', {
      menuId: menuUuid,
      deliveryDate: basketDate,
    })

    const validRecipesInBasketUuids = validBasketRecipes.flatMap((r) =>
      Array(r.quantity).fill(r.recipe.id),
    )

    queueTrackingEvent<ShowBasketEvent>({
      action: 'show_basket',
      properties: {
        menu_uuid: menuUuid,
        basket_recipe_count: validRecipesInBasketUuids.length,
        recipes_in_basket: validRecipesInBasketUuids.join(','),
      },
    })
  }

  const updateBasketRecipePortions = (recipeId: RecipeUUID, updatedNumPortions: number) => {
    updateRecipePortionsByUuid(recipeId, updatedNumPortions)
    void refreshRecipeTile(
      recipeId,
      updatedNumPortions as MenuRecipeTileRequestParams['numPortions'],
    )
  }

  return (
    <Modal isOpen name="view-basket-modal" onAfterClose={onCloseModal} onAfterOpen={trackModalOpen}>
      <Box
        data-testid="view-basket-content"
        display={isDetailOpen ? Display.None : Display.Flex}
        flexDirection={FlexDirection.Column}
        flexGrow={1}
        flexShrink={1}
        hidden={isDetailOpen}
        minHeight="0"
        overflowY={Overflow.Hidden}
      >
        <ScrollableBasketModalContent
          name={user}
          maxRecipesInBox={maxRecipesInBox}
          basketRecipes={validBasketRecipes}
          menuUuid={menuUuid}
          numPortions={numPortions}
          closeModal={onCloseModal}
          addRecipe={addRecipeByUuid}
          removeRecipe={removeRecipeByUuid}
          updateRecipePortions={updateBasketRecipePortions}
          onClickRecipe={(recipeUuid) => setRecipeDetailsId(recipeUuid)}
          onCheckout={onCheckout}
          deliverySlot={deliverySlot as { date: string; timeStart: string; timeEnd: string }}
          deliveryAddress={deliveryAddress}
          deliveryPricing={deliveryPricing}
          isCheckoutDisabled={isCheckoutDisabled}
          isCheckoutPending={isCheckoutPending}
          isTransactionalOrder={isTransactionalOrder}
          basketUpsell={basketUpsell}
          isFlexEnabled={isFlexEnabled}
          isValueBannerEnabled={isValueBannerEnabled}
        />
      </Box>
      {recipeDetailsId && (
        <RecipeDetail onClose={() => setRecipeDetailsId(null)} recipeId={recipeDetailsId} />
      )}
      {!recipeDetailsId && productDetail && (
        <BasketProductDetail
          isAvailable={productDetail.isAvailable}
          limitReached={productDetail.limitReached}
          onAdd={productDetail.onAdd}
          onClose={productDetail.onClose}
          onRemove={productDetail.onRemove}
          outOfStock={productDetail.outOfStock}
          product={productDetail.product}
          qty={productDetail.qty}
        />
      )}
    </Modal>
  )
}
